"use client";

import React, { createContext, useContext, useMemo } from "react";
import useStudioSituationActionHandlers from "../hooks/useStudioSituationActionHandlers";
import useStudioThoughtSelectionActions from "../hooks/useStudioThoughtSelectionActions";
import { useStudioConfirmationContext } from "./StudioConfirmationProvider";
import { useStudioReassignStateContext } from "./StudioReassignStateProvider";

interface StudioHandlersValue {
  thoughtHandlers: ReturnType<typeof useStudioThoughtSelectionActions>;
  situationHandlers: ReturnType<typeof useStudioSituationActionHandlers>;
}

const StudioHandlersContext = createContext<StudioHandlersValue | undefined>(
  undefined,
);

export function StudioHandlersProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    confirmSelectedThoughtsDeletion,
    confirmSelectedSuggestedThoughtsDeletion,
    confirmSelectedThoughtsResetReflections,
    confirmSelectedThoughtsConfirmation,
    confirmSituationsDeletion,
  } = useStudioConfirmationContext();
  const { setSelectedThoughtConfirmationPrototypes } =
    useStudioReassignStateContext();

  const thoughtHandlers = useStudioThoughtSelectionActions({
    confirmSelectedThoughtsDeletion,
    confirmSelectedSuggestedThoughtsDeletion,
    confirmSelectedThoughtsResetReflections,
    confirmSelectedThoughtsConfirmation,
    setConfirmSelectedThoughtPrototypes: setSelectedThoughtConfirmationPrototypes,
  });
  const situationHandlers = useStudioSituationActionHandlers({
    confirmSituationsDeletion,
  });

  const handlersValue = useMemo(
    () => ({
      thoughtHandlers,
      situationHandlers,
    }),
    [thoughtHandlers, situationHandlers],
  );

  return (
    <StudioHandlersContext.Provider value={handlersValue}>
      {children}
    </StudioHandlersContext.Provider>
  );
}

export function useStudioHandlersContext() {
  const context = useContext(StudioHandlersContext);

  if (context === undefined) {
    throw new Error(
      "useStudioHandlersContext must be used within a StudioHandlersProvider",
    );
  }

  return context;
}
